/**
 * Секция "Прочее"
 */

import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import {
  useDownloadRegistryPack,
  useMpl80State,
  useToggleMpl80,
  useMpl5080State,
  useToggleMpl5080,
  usePaymentState,
  useTogglePaymentState,
  useRateFilters,
  useToggleRateFilter,
} from '../../../hooks/useAdmin';
import { DynamicPropertyToggle } from '../forms/DynamicPropertyToggle';
import { GenerateDocumentFormComponent } from '../forms/GenerateDocumentForm';

interface OtherSectionProps {
  creditContractId: number | null;
}

/**
 * Компонент секции "Прочее"
 */
export const OtherSection: React.FC<OtherSectionProps> = ({ creditContractId }) => {
  const [requestsLocked, setRequestsLocked] = useState(false);

  const downloadRegistryPackMutation = useDownloadRegistryPack();

  const { data: mpl80State = false } = useMpl80State();
  const toggleMpl80Mutation = useToggleMpl80();

  const { data: mpl5080State = false } = useMpl5080State();
  const toggleMpl5080Mutation = useToggleMpl5080();

  const { data: paymentState = false } = usePaymentState();
  const togglePaymentStateMutation = useTogglePaymentState();

  const { data: rateFilters = [] } = useRateFilters();
  const toggleRateFilterMutation = useToggleRateFilter();

  // Сбрасываем состояние мутации при смене клиента
  React.useEffect(() => {
    downloadRegistryPackMutation.reset();
  }, [creditContractId]);

  const handleToggle = async (toggleFn: () => Promise<any>) => {
    setRequestsLocked(true);
    try {
      await toggleFn();
    } finally {
      setTimeout(() => {
        setRequestsLocked(false);
      }, 2000);
    }
  };

  return (
    <div>

      {/* Скачивание архива реестров */}
      <fieldset>
        <Formik
          initialValues={{}}
          onSubmit={async () => {
            if (creditContractId) {
              await downloadRegistryPackMutation.mutateAsync(creditContractId);
            }
          }}
        >
          {() => (
            <Form>
              <div style={{ display: 'table' }}>
                <div style={{ marginLeft: '10px', fontWeight: 'bold' }}>
                  Скачивание архива реестров, направленных в ИБСО по сделке
                </div>
                <div style={{ marginTop: '20px' }} />

                <button
                  className="btn"
                  type="submit"
                  disabled={!creditContractId || downloadRegistryPackMutation.isPending}
                  style={{ marginLeft: '10px' }}
                >
                  {downloadRegistryPackMutation.isPending
                    ? 'Скачивание...'
                    : 'Скачать архив с реестрами'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </fieldset>

      {/* МПЛ 80 */}
      <DynamicPropertyToggle
        title="Проверка превышения допустимого значения ПДН 80"
        checked={mpl80State}
        onToggle={() => handleToggle(() => toggleMpl80Mutation.mutateAsync())}
        disabled={requestsLocked}
      />

      {/* МПЛ 50-80 */}
      <DynamicPropertyToggle
        title="Проверка превышения допустимого значения ПДН 50-80"
        checked={mpl5080State}
        onToggle={() => handleToggle(() => toggleMpl5080Mutation.mutateAsync())}
        disabled={requestsLocked}
      />

      {/* Автоматическая отправка платежей */}
      <DynamicPropertyToggle
        title="Автоматическая отправка платежей"
        checked={paymentState}
        onToggle={() => handleToggle(() => togglePaymentStateMutation.mutateAsync())}
        disabled={requestsLocked}
      />

      {/* Фильтры заявок по ставкам */}
      <fieldset>
        <div style={{ display: 'table' }}>
          <div style={{ marginLeft: '10px', fontWeight: 'bold' }}>
            Текущее состояние фильтров заявок по ставкам
            <br />
            (Включено - значит не принимаем заявку с такой ставкой)
          </div>
          <div style={{ marginLeft: '10px', marginTop: '10px' }}>
            {rateFilters.map((item) => (
              <div key={item.id} style={{ marginBottom: '8px' }}>
                <FormControlLabel
                  control={
                    <Switch
                      onChange={() => {
                        handleToggle(() => toggleRateFilterMutation.mutateAsync(item.id));
                      }}
                      checked={item.enabled}
                      disabled={requestsLocked}
                      color="primary"
                    />
                  }
                  label={item.description}
                />
              </div>
            ))}
          </div>
        </div>
      </fieldset>

      {/* Генерация документов */}
      <GenerateDocumentFormComponent />
    </div>
  );
};
